import { useRef, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Plan } from "@/config/subscriptionPlans";
import { SubscriptionPlan } from "@/server/types/subscription";
import PlanCard from "./PlanCard";

interface MobilePlanSwiperProps {
  plans: Plan[];
  currentPlanId: SubscriptionPlan | null;
  selectedId: SubscriptionPlan | null;
  onSelectId: (id: SubscriptionPlan) => void;
  isYearly: boolean;
  recommendedId: SubscriptionPlan | null;
  isDisabled: boolean;
}

export default function MobilePlanSwiper({
  plans,
  currentPlanId,
  selectedId,
  onSelectId,
  isYearly,
  recommendedId,
  isDisabled,
}: MobilePlanSwiperProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);
  const userIsScrolling = useRef(false);
  const slotWidthRef = useRef(0);
  const [visibleIdx, setVisibleIdx] = useState(() =>
    Math.max(
      0,
      plans.findIndex((p) => p.id === selectedId),
    ),
  );

  // Cache slot width; update on resize to avoid getBoundingClientRect in scroll handler
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !el.firstElementChild) {
      return;
    }
    const update = () => {
      slotWidthRef.current =
        (el.firstElementChild as HTMLElement).getBoundingClientRect().width +
        12;
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Programmatic scroll when selection changes externally (e.g. dot tap)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !el.firstElementChild) {
      return;
    }
    if (userIsScrolling.current) {
      userIsScrolling.current = false;
      return;
    }
    const selectedIdx = plans.findIndex((p) => p.id === selectedId);
    if (selectedIdx < 0) {
      return;
    }
    el.scrollTo({
      left: selectedIdx * slotWidthRef.current,
      behavior: didInitialScroll.current ? "smooth" : "auto",
    });
    didInitialScroll.current = true;
    setVisibleIdx(selectedIdx);
  }, [selectedId, plans]);

  function onScroll() {
    const el = scrollRef.current;
    if (!el || slotWidthRef.current === 0) {
      return;
    }
    const idx = Math.round(el.scrollLeft / slotWidthRef.current);
    if (idx < 0 || idx >= plans.length) {
      return;
    }
    if (idx !== visibleIdx) {
      setVisibleIdx(idx);
    }
    if (!isDisabled && plans[idx]?.id !== selectedId) {
      userIsScrolling.current = true;
      onSelectId(plans[idx].id);
    }
  }

  function jumpTo(i: number) {
    const el = scrollRef.current;
    if (!el || slotWidthRef.current === 0) {
      return;
    }
    el.scrollTo({ left: i * slotWidthRef.current, behavior: "smooth" });
    setVisibleIdx(i);
    if (!isDisabled) {
      onSelectId(plans[i].id);
    }
  }

  return (
    <div className="pb-3 pt-1">
      {/* Scroll container */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex gap-3 overflow-x-auto scroll-smooth px-4 py-3"
        style={{
          scrollSnapType: "x mandatory",
          scrollPaddingLeft: "1rem",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {plans.map((plan) => (
          <div
            key={plan.id}
            style={{ scrollSnapAlign: "start", scrollSnapStop: "always" }}
            className="w-[calc(100%-28px)] shrink-0"
          >
            <PlanCard
              plan={plan}
              isYearly={isYearly}
              isSelected={plan.id === selectedId}
              isCurrentPlan={plan.id === currentPlanId}
              isRecommended={plan.id === recommendedId}
              onClick={() => onSelectId(plan.id)}
              isDisabled={isDisabled}
            />
          </div>
        ))}
      </div>

      {/* Segmented stepper */}
      <div
        role="tablist"
        aria-label="Plans"
        className="mx-4 mt-3 flex items-stretch gap-0.5 rounded-full border border-border bg-muted/60 p-0.5"
      >
        {plans.map((plan, i) => (
          <button
            key={plan.id}
            type="button"
            role="tab"
            aria-selected={i === visibleIdx}
            onClick={() => jumpTo(i)}
            className={cn(
              "flex-1 rounded-full py-1.5 text-[11px] font-medium transition-all duration-150",
              i === visibleIdx
                ? "bg-secondary/20 font-semibold text-secondary"
                : "text-muted-foreground",
            )}
          >
            {plan.name}
          </button>
        ))}
      </div>
    </div>
  );
}
